//
// Design System — VisiMe Web
// Desktop: Substack structure + VisiMe colors (never Substack orange).
// Structure refs: screenshots/parity/web/mobbin-inspiration-desktop/substack*
// Brand bright #268FB6; UI primary is AA-safe #1F7A9B; canvas/surface follow mode
// Keep every key — incomplete tokens previously broke the app.
//

import type { ColorMode } from './config/themeMode';

const shared = {
  // Filled controls (buttons/chips): dark teal so white label stays ≥4.5:1.
  // Link/icon `primary` is mode-specific (see light/dark palettes).
  brandBright: '#268FB6',
  action: '#1F7A9B',
  actionDark: '#186480',
  actionLight: 'rgba(31, 122, 155, 0.18)',
  onPrimary: '#FFFFFF',
  success: '#21A34A',
  warning: '#B8860B',
  iconTile: {
    blue: '#1F7A9B',
    teal: '#1A7A8A',
    green: '#1E7838',
    coral: '#D70015',
    orange: '#C93400',
    slate: '#636366',
    ink: '#5856D6',
    sky: '#0071A4',
  },
} as const;

const darkColors = {
  ...shared,
  canvas: '#000000',
  surface: '#1A1A1A',
  surfaceElevated: '#262626',
  primary: '#6EC8E8',
  primaryLight: 'rgba(110, 200, 232, 0.18)',
  primaryDark: '#4EB3D4',
  // Bright destructive on dark surfaces (≥4.5:1 on #1A1A1A).
  error: '#FF6B6B',
  errorSoft: 'rgba(255, 107, 107, 0.14)',
  textPrimary: '#FFFFFF',
  textSecondary: '#C7C7CC',
  textTertiary: '#AEAEB2',
  border: 'rgba(255,255,255,0.12)',
  separator: 'rgba(255,255,255,0.14)',
  hover: 'rgba(255,255,255,0.08)',
  hoverStrong: 'rgba(255,255,255,0.12)',
  surfaceDark: '#121417',
  surfaceElevatedDark: '#1C1F23',
  textPrimaryDark: '#E8EAEC',
  textSecondaryDark: '#9AA3A8',
  borderDark: '#3A4146',
  separatorDark: 'rgba(70, 70, 70, 0.75)',
};

const lightColors = {
  ...shared,
  canvas: '#F2F2F7',
  surface: '#FFFFFF',
  surfaceElevated: '#FFFFFF',
  primary: '#1A6B88',
  primaryLight: 'rgba(26, 107, 136, 0.16)',
  primaryDark: '#155A75',
  // Darker destructive so body text meets AA on white (was #FF3B30 ≈ 3.55:1).
  error: '#D70015',
  errorSoft: 'rgba(215, 0, 21, 0.08)',
  textPrimary: '#111111',
  textSecondary: '#3A3A3C',
  textTertiary: '#48484A',
  border: 'rgba(0,0,0,0.12)',
  separator: 'rgba(0,0,0,0.14)',
  hover: 'rgba(0,0,0,0.05)',
  hoverStrong: 'rgba(0,0,0,0.08)',
  surfaceDark: '#121417',
  surfaceElevatedDark: '#1C1F23',
  textPrimaryDark: '#E8EAEC',
  textSecondaryDark: '#9AA3A8',
  borderDark: '#3A4146',
  separatorDark: 'rgba(70, 70, 70, 0.75)',
};

const darkDesktop = {
  navActiveBg: '#1A1A1A',
  navActiveWeight: 700,
  createRadius: '999px',
  companionCardRadius: '12px',
  searchRadius: '999px',
  useHairlineBorders: false,
  cardBorder: 'none',
};

const lightDesktop = {
  ...darkDesktop,
  navActiveBg: '#E8E8ED',
};

const darkShadows = {
  none: 'none',
  sm: '0 1px 2px rgba(0, 0, 0, 0.35)',
  md: '0 4px 16px rgba(0, 0, 0, 0.4)',
  lg: '0 12px 32px rgba(0, 0, 0, 0.45)',
  xl: '0 20px 40px rgba(0, 0, 0, 0.5)',
};

const lightShadows = {
  none: 'none',
  sm: '0 1px 2px rgba(0, 0, 0, 0.06)',
  md: '0 4px 16px rgba(0, 0, 0, 0.08)',
  lg: '0 12px 32px rgba(0, 0, 0, 0.1)',
  xl: '0 20px 40px rgba(0, 0, 0, 0.12)',
};

type DesignColors = typeof darkColors;

let activeMode: ColorMode = 'dark';
let activeColors: DesignColors = darkColors;
let activeDesktop = darkDesktop;
let activeShadows = darkShadows;

export function applyColorMode(mode: ColorMode): void {
  activeMode = mode;
  activeColors = mode === 'light' ? lightColors : darkColors;
  activeDesktop = mode === 'light' ? lightDesktop : darkDesktop;
  activeShadows = mode === 'light' ? lightShadows : darkShadows;
  if (typeof document !== 'undefined') {
    document.documentElement.dataset.theme = mode;
    document.documentElement.style.colorScheme = mode;
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', mode === 'dark' ? '#000000' : '#F2F2F7');
  }
}

export const DesignSystem = {
  get mode(): ColorMode {
    return activeMode;
  },
  get colors(): DesignColors {
    return activeColors;
  },
  get desktop() {
    return activeDesktop;
  },
  get shadows() {
    return activeShadows;
  },

  typography: {
    displayLarge: { fontSize: '34px', fontWeight: 700, lineHeight: 1.15 },
    displayMedium: { fontSize: '28px', fontWeight: 700, lineHeight: 1.2 },
    h1: { fontSize: '24px', fontWeight: 700, lineHeight: 1.25 },
    h2: { fontSize: '20px', fontWeight: 600, lineHeight: 1.3 },
    h3: { fontSize: '17px', fontWeight: 600, lineHeight: 1.35 },
    bodyLarge: { fontSize: '17px', fontWeight: 400, lineHeight: 1.5 },
    body: { fontSize: '15px', fontWeight: 400, lineHeight: 1.5 },
    bodySmall: { fontSize: '13px', fontWeight: 400, lineHeight: 1.45 },
    label: { fontSize: '15px', fontWeight: 500, lineHeight: 1.4 },
    labelSmall: { fontSize: '13px', fontWeight: 500, lineHeight: 1.4 },
    caption: { fontSize: '12px', fontWeight: 400, lineHeight: 1.4 },
    buttonLarge: { fontSize: '17px', fontWeight: 600, lineHeight: 1.2 },
    button: { fontSize: '15px', fontWeight: 600, lineHeight: 1.2 },
    buttonSmall: { fontSize: '13px', fontWeight: 600, lineHeight: 1.2 },
    pageTitle: { fontSize: '22px', fontWeight: 700, lineHeight: 1.2, letterSpacing: '-0.03em' },
    sectionLabel: {
      fontSize: '12px',
      fontWeight: 650,
      lineHeight: 1.3,
      letterSpacing: '0.06em',
      textTransform: 'uppercase' as const,
    },
    rowTitle: { fontSize: '15px', fontWeight: 650, lineHeight: 1.25 },
    rowMeta: { fontSize: '13px', fontWeight: 400, lineHeight: 1.35 },
  },

  layout: {
    pageGap: '20px',
    sectionGap: '16px',
    cardPad: '14px',
    rowPadY: '12px',
    rowPadX: '14px',
    sidebarWidth: 288,
    companionWidth: 320,
    feedWidth: 600,
    companionGap: '22px',
    feedPostGap: '20px',
  },

  spacing: {
    xs: '4px',
    sm: '8px',
    md: '12px',
    lg: '16px',
    xl: '24px',
    xxl: '32px',
    xxxl: '48px',
  },

  padding: {
    button: {
      paddingTop: '14px',
      paddingBottom: '14px',
      paddingLeft: '20px',
      paddingRight: '20px',
    },
    card: {
      paddingTop: '16px',
      paddingBottom: '16px',
      paddingLeft: '16px',
      paddingRight: '16px',
    },
    cell: {
      paddingTop: '12px',
      paddingBottom: '12px',
      paddingLeft: '16px',
      paddingRight: '16px',
    },
    screen: {
      paddingTop: '16px',
      paddingBottom: '16px',
      paddingLeft: '20px',
      paddingRight: '20px',
    },
  },

  borderRadius: {
    xs: '4px',
    sm: '6px',
    md: '10px',
    lg: '12px',
    xl: '16px',
    pill: '999px',
    full: '9999px',
    media: '12px',
  },

  animation: {
    fast: '0.15s',
    normal: '0.22s',
    slow: '0.35s',
    easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
    springEasing: 'cubic-bezier(0.34, 1.2, 0.64, 1)',
  },

  motion: {
    duration: '220ms',
    easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
    springEasing: 'cubic-bezier(0.34, 1.2, 0.64, 1)',
  },
};

export default DesignSystem;
